import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import plannerApi, { track, guestId } from "@/lib/plannerApi";

// Loads one essentials list and autosaves edits (debounced, version-locked).
export function usePlannerList(id) {
    const [list, setList] = useState(null);
    const [items, setItemsState] = useState([]);
    const [meta, setMetaState] = useState({ name: "", departments: [] });
    const [status, setStatus] = useState("saved");
    const [canUndo, setCanUndo] = useState(false);
    const version = useRef(1);
    const seq = useRef(0);
    const timer = useRef(null);
    const history = useRef([]);
    const pending = useRef({});
    const savedOnce = useRef(false);
    const latest = useRef({ items: [], meta: {} });

    const load = useCallback(() => plannerApi.get(`/lists/${id}`).then((r) => {
        setList(r.data);
        setItemsState(r.data.items);
        setMetaState({ name: r.data.name, departments: r.data.departments || [] });
        latest.current = { items: r.data.items, meta: { name: r.data.name, departments: r.data.departments || [] } };
        version.current = r.data.version;
        setStatus("saved");
    }), [id]);

    useEffect(() => { load().catch((e) => toast.error(e.response?.data?.detail || "Couldn't open this list")); }, [load]);

    const save = useCallback(async () => {
        const mySeq = seq.current;
        const body = { version: version.current, ...pending.current };
        pending.current = {};
        setStatus("saving");
        try {
            const r = await plannerApi.put(`/lists/${id}`, body);
            version.current = r.data.version;
            if (mySeq === seq.current) {
                setItemsState(r.data.items);
                latest.current.items = r.data.items;
                setStatus("saved");
            }
            setList((l) => ({ ...l, ...r.data, items: undefined }));
            if (!savedOnce.current) { savedOnce.current = true; track("list_saved", r.data.planner_type); }
        } catch (e) {
            if (e.response?.status === 409) {
                toast.error(e.response.data.detail);
                load();
            } else {
                pending.current = { ...body, ...pending.current };
                delete pending.current.version;
                setStatus("error");
            }
        }
    }, [id, load]);

    const schedule = useCallback(() => {
        seq.current += 1;
        setStatus("dirty");
        clearTimeout(timer.current);
        timer.current = setTimeout(save, 900);
    }, [save]);

    const setItems = useCallback((updater) => {
        const prev = latest.current.items;
        const next = typeof updater === "function" ? updater(prev) : updater;
        history.current.push(prev);
        if (history.current.length > 30) history.current.shift();
        setCanUndo(true);
        if (prev.length === 0 && next.length > 0) track("first_item_added", list?.planner_type);
        if (prev.length < 10 && next.length >= 10) track("ten_items_added", list?.planner_type);
        latest.current.items = next;
        setItemsState(next);
        pending.current.items = next;
        schedule();
    }, [schedule, list?.planner_type]);

    const undo = useCallback(() => {
        const prev = history.current.pop();
        if (!prev) return;
        setCanUndo(history.current.length > 0);
        latest.current.items = prev;
        setItemsState(prev);
        pending.current.items = prev;
        schedule();
    }, [schedule]);

    const setMeta = useCallback((patch) => {
        latest.current.meta = { ...latest.current.meta, ...patch };
        setMetaState(latest.current.meta);
        Object.assign(pending.current, patch);
        schedule();
    }, [schedule]);

    // Flush unsaved edits when leaving the page or closing the tab.
    useEffect(() => {
        const flushBeacon = () => {
            if (!Object.keys(pending.current).length) return;
            fetch(`${plannerApi.defaults.baseURL}/lists/${id}`, {
                method: "PUT", keepalive: true, credentials: "include",
                headers: { "Content-Type": "application/json", "X-Planner-Guest": guestId() },
                body: JSON.stringify({ version: version.current, ...pending.current }),
            });
            pending.current = {};
        };
        window.addEventListener("beforeunload", flushBeacon);
        return () => {
            window.removeEventListener("beforeunload", flushBeacon);
            clearTimeout(timer.current);
            if (Object.keys(pending.current).length) save();
        };
    }, [id, save]);

    return { list, items, meta, status, canUndo, setItems, setMeta, undo, retry: save };
}
